import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { MuiFormInput } from 'components/Form';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import type { GetApprovalTicketFormValuesT } from './declarations';
import useTicketApprovePopup from './hooks/useTicketApprovePopup';
import { getPopupContentStyle } from './TicketApprovePopup.styled';
import validationSchema from './validationSchema';

type TicketApprovePopupPropsT = {
  ticketId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const TicketApprovePopup: FC<TicketApprovePopupPropsT> = ({ ticketId, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const { isApprovingTicket, handleSubmitForm, handleClosePopup } = useTicketApprovePopup(ticketId);

  const fromMethods = useForm<GetApprovalTicketFormValuesT>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Variables
   */
  const { handleSubmit, reset } = fromMethods;

  const getSubmitButtonText = (): string => {
    if (isApprovingTicket) return 'Requesting...';
    return isMobile ? 'Request' : 'Get Approval';
  };

  useEffect(
    () => () => {
      if (isOpen) {
        reset();
      }
    },
    [isOpen, reset]
  );

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn
      modalTitle="Request Override Approval"
      size={ModalSizeE.REQUEST_OVERRIDE_APPROVAL}
      popupId={ScrollLockIdE.TicketApprovePopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      <FormProvider {...fromMethods}>
        <Box
          component="form"
          onSubmit={handleSubmit(data => handleSubmitForm(data, onClose))}
          sx={{ width: '100%' }}
        >
          <Box sx={{ mb: 1.5 }}>
            <MuiFormInput name="privateMessage" label="Override Reason" />
          </Box>
          <Box display="flex" justifyContent={isMobile ? 'center' : 'flex-end'} sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={isApprovingTicket}>
              {getSubmitButtonText()}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Popup>
  );
};

export default TicketApprovePopup;
